// styles.ts
import styled, { css } from "styled-components"

export const colors = {
  primary: "#6c5ce7",
  primaryHover: "#8075e5",
  secondary: "#5a4ebd",
  background: "#1e1a2e",
  surface: "#2d2640",
  surfaceLight: "#3a3053",
  text: "#FFFFFF",
  textSecondary: "#c8c5e8",
  border: "rgba(108, 92, 231, 0.2)",
  progressBg: "#3a3053",
  progressFill: "#fff",
}

const flexCenter = css`
  display: flex;
  align-items: center;
`

export const PlayerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background-color: ${colors.background};
  border: 1px solid ${colors.border};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  max-width: 100%;
  overflow: hidden;
`

export const TrackInfo = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ControlsRow = styled.div`
  ${flexCenter}
  gap: 16px;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

export const MainControls = styled.div`
  ${flexCenter}
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    order: 1;
  }
`

export const SecondaryControls = styled.div`
  ${flexCenter}
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    order: 3;
    margin-top: 8px;
  }
`

export const ProgressContainer = styled.div`
  ${flexCenter}
  gap: 12px;
  flex: 1;
  width: 100%;

  @media (max-width: 768px) {
    order: 2;
    width: 100%;
  }
`

export const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: ${colors.primary};
  color: ${colors.text};
  cursor: pointer;
  ${flexCenter}
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.primaryHover};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`

export const Time = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  min-width: 42px;
  text-align: center;
`

export const ProgressBarContainer = styled.div`
  position: relative;
  flex: 1;
  height: 4px;
  border-radius: 3px;
  background-color: ${colors.progressBg};
  cursor: pointer;
`

export const ProgressBarFill = styled.div<{ progress: number }>`
  position: absolute;
  height: 100%;
  width: ${(props) => props.progress}%;
  background-color: ${colors.progressFill};
  border-radius: 4px;
  transition: background-color 0.2s ease, width 0.1s linear;

  ${ProgressBarContainer}:hover & {
    background-color: ${colors.primary};
  }
`

export const ProgressBarHandle = styled.div<{ progress: number }>`
  position: absolute;
  top: 50%;
  left: ${(props) => props.progress}%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: white;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ProgressBarContainer}:hover & {
    opacity: 1;
  }
`

export const ProgressInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  margin: 0;
  padding: 0;
`

export const SpeedSelector = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  background: ${colors.surfaceLight};
  font-size: 13px;
  border: 1px solid ${colors.border};
  color: ${colors.text};
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.secondary};
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`

export const SpeedControl = styled.div`
  ${flexCenter}
  gap: 8px;
  font-size: 13px;
  color: ${colors.textSecondary};
  white-space: nowrap;
`

export const DownloadButton = styled.a`
  ${flexCenter}
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${colors.surfaceLight};
  color: ${colors.text};
  font-size: 13px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  gap: 6px;
  position: relative;

  &:hover {
    background-color: ${colors.secondary};
  }
`

// ⬇️ Tooltips y banners

export const OptimizationBanner = styled.div`
  ${flexCenter}
  background-color: rgba(108, 92, 231, 0.1);
  color: ${colors.textSecondary};
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  gap: 8px;
  backdrop-filter: blur(5px);
  margin-top: 12px;
  border: 1px solid rgba(108, 92, 231, 0.2);
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: rgba(108, 92, 231, 0.15)

  }
`

export const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover > div {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
`

export const TooltipText = styled.div`
  position: absolute;
  bottom: 130%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${colors.surface};
  color: ${colors.text};
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 12px;
  white-space: normal;
  line-height: 1.5;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 20;
  opacity: 0;
  transition: all 0.2s ease;
  pointer-events: none;
  width: 240px;
  border: 1px solid ${colors.border};

  @media (max-width: 768px) {
    width: 220px;
    bottom: 120%;
  }
`

export const MobileTooltipText = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${colors.surface};
  color: ${colors.text};
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  white-space: normal;
  line-height: 1.5;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 100;
  max-width: 85%;
  border: 1px solid ${colors.border};
  text-align: center;
`

export const MobileTooltipOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const MobileTooltipCloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${colors.textSecondary};
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
`

export const DownloadTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 6px;

  &:hover > div {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
`

export const DownloadTooltipText = styled.div`
  position: absolute;
  top: -10px;
  right: 0;
  transform: translateY(-100%);
  background-color: ${colors.surface};
  color: ${colors.text};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  line-height: 1.5;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 20;
  opacity: 0;
  transition: all 0.2s ease;
  pointer-events: none;
  border: 1px solid ${colors.border};
`
